import { useState } from "react";
import { Plus, Trash2, FileText, Download } from "lucide-react";
import { Table, Button, Modal, Form, Input, InputNumber, Select, DatePicker, Popconfirm, message } from "antd";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import type { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { IWorkerPayslip } from "../../../types/api/payslips";
import { usePayslipList, usePayslipCreate, usePayslipDelete } from "../../../hooks/api/payslips/hooks";
import { useFieldWorkerList } from "../../../hooks/api/field-workers/hooks";

function AddPayslipModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data: workerRes } = useFieldWorkerList({ page: 1, limit: 100 });
  const workers = (workerRes?.data as any[]) ?? [];

  const create = usePayslipCreate({
    onSuccess: () => { msgApi.success("Payslip added."); onClose(); form.resetFields(); },
    onError: (e) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  return (
    <Modal
      open={open}
      onCancel={() => { onClose(); form.resetFields(); }}
      title="Add Payslip"
      width={520}
      footer={[
        <Button key="cancel" onClick={() => { onClose(); form.resetFields(); }}>Cancel</Button>,
        <Button key="save" type="primary" loading={create.isPending} onClick={() => form.submit()}>Save Payslip</Button>,
      ]}
    >
      {ctx}
      <Form
        form={form}
        layout="vertical"
        size="middle"
        className="pt-2"
        onFinish={(vals) => {
          create.mutate({
            ...vals,
            pay_period: vals.pay_period?.format("YYYY-MM"),
            period_start: vals.period_range?.[0]?.format("YYYY-MM-DD"),
            period_end: vals.period_range?.[1]?.format("YYYY-MM-DD"),
          });
        }}
      >
        <Form.Item name="user_id" label="Worker" rules={[{ required: true }]}>
          <Select
            showSearch
            placeholder="Select worker…"
            filterOption={(v, o) => (o?.label as string)?.toLowerCase().includes(v.toLowerCase())}
            options={workers.map((w: any) => ({
              value: w.id,
              label: [w.first_name, w.last_name].filter(Boolean).join(" ") || w.email,
            }))}
          />
        </Form.Item>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="pay_period" label="Pay Period (Month)" rules={[{ required: true }]}>
            <DatePicker picker="month" className="w-full" />
          </Form.Item>
          <Form.Item name="period_range" label="Period Start–End" rules={[{ required: true }]}>
            <DatePicker.RangePicker className="w-full" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-3 gap-x-3">
          <Form.Item name="gross_pay" label="Gross Pay (£)" rules={[{ required: true }]}>
            <InputNumber className="w-full" min={0} step={0.01} prefix="£" />
          </Form.Item>
          <Form.Item name="deductions" label="Deductions (£)">
            <InputNumber className="w-full" min={0} step={0.01} prefix="£" />
          </Form.Item>
          <Form.Item name="net_pay" label="Net Pay (£)" rules={[{ required: true }]}>
            <InputNumber className="w-full" min={0} step={0.01} prefix="£" />
          </Form.Item>
        </div>
        <Form.Item name="pdf_url" label="PDF URL (optional)">
          <Input placeholder="https://... or leave blank" />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

const PayslipsPage = () => {
  const [workerFilter, setWorkerFilter] = useState<number | undefined>(undefined);
  const [periodFilter, setPeriodFilter] = useState<string | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [msgApi, ctx] = message.useMessage();

  const { data: workerRes } = useFieldWorkerList({ page: 1, limit: 100 });
  const workers = (workerRes?.data as any[]) ?? [];

  const qData = { page: 1, limit: 20, ...(workerFilter && { user_id: workerFilter }), ...(periodFilter && { pay_period: periodFilter }) };
  const { data: res, isLoading, refetchWithQuery } = usePayslipList(qData);
  const payslips: IWorkerPayslip[] = (res?.data as any) ?? [];
  const total = Number(res?.extra?.total ?? 0);

  const deletePs = usePayslipDelete({
    onSuccess: () => { msgApi.success("Payslip removed."); refetchWithQuery(qData); },
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Delete failed"),
  });

  const columns: ColumnType<IWorkerPayslip>[] = [
    {
      title: "Worker",
      dataIndex: "worker_name",
      key: "worker_name",
      render: (v) => <span className="font-medium text-slate-800 text-sm">{v || "—"}</span>,
    },
    {
      title: "Pay Period",
      dataIndex: "pay_period",
      key: "pay_period",
      render: (v, r) => (
        <div>
          <div className="font-semibold text-slate-700">{dayjs(v, "YYYY-MM").format("MMM YYYY")}</div>
          <div className="text-xs text-slate-400">{dayjs(r.period_start).format("DD MMM")} – {dayjs(r.period_end).format("DD MMM YYYY")}</div>
        </div>
      ),
      width: 140,
    },
    {
      title: "Gross Pay",
      dataIndex: "gross_pay",
      key: "gross_pay",
      render: (v) => <span className="text-slate-700">£{Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>,
      width: 110,
    },
    {
      title: "Deductions",
      dataIndex: "deductions",
      key: "deductions",
      render: (v) => <span className="text-red-500">-£{Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>,
      width: 110,
    },
    {
      title: "Net Pay",
      dataIndex: "net_pay",
      key: "net_pay",
      render: (v) => <span className="font-bold text-slate-800">£{Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>,
      width: 110,
    },
    {
      title: "PDF",
      dataIndex: "pdf_url",
      key: "pdf_url",
      render: (v) => v
        ? <a href={v} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 text-xs hover:underline"><Download size={11} />Download</a>
        : <span className="text-slate-300 text-xs">—</span>,
      width: 90,
    },
    {
      title: "Added",
      dataIndex: "created_at",
      key: "created_at",
      render: (v) => <span className="text-xs text-slate-400">{dayjs(v).format("DD MMM YYYY")}</span>,
      width: 110,
    },
    {
      title: "",
      key: "actions",
      width: 50,
      render: (_, r) => (
        <Popconfirm title="Delete this payslip?" onConfirm={() => deletePs.mutate(r.id)} okText="Delete" okType="danger">
          <Button size="small" danger icon={<Trash2 size={11} />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="p-6 min-h-screen">
      {ctx}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-slate-800">Payslips</h1>
          </div>
          <p className="text-slate-500 text-sm">{total} records · Worker pay records</p>
        </div>
        <ButtonWrapper type="primary" icon={<Plus size={15} />} onClick={() => setModalOpen(true)} className="flex items-center gap-2">
          Add Payslip
        </ButtonWrapper>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Select
          placeholder="All Workers"
          value={workerFilter}
          onChange={(v) => { setWorkerFilter(v); refetchWithQuery({ ...qData, user_id: v }); }}
          allowClear
          style={{ width: 200 }}
          showSearch
          filterOption={(val, o) => (o?.label as string)?.toLowerCase().includes(val.toLowerCase())}
          options={workers.map((w: any) => ({
            value: w.id,
            label: [w.first_name, w.last_name].filter(Boolean).join(" ") || w.email,
          }))}
        />
        <DatePicker
          picker="month"
          placeholder="Filter by month"
          onChange={(d) => {
            const period = d ? d.format("YYYY-MM") : undefined;
            setPeriodFilter(period);
            refetchWithQuery({ ...qData, pay_period: period });
          }}
        />
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <Table
          dataSource={payslips}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 20, total, showTotal: (t) => `${t} payslips`, showSizeChanger: false }}
          scroll={{ x: 800 }}
        />
      </div>

      <AddPayslipModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default PayslipsPage;
